import React, { useEffect, useState } from 'react';
import { ArrowLeft, Calculator, Check, CheckCircle2, PlayCircle } from 'lucide-react';
import { FixedAsset, DepreciationEntry } from '../../../../types/FixedAsset.types';
import { CostCenter } from '../../../../types/CostCenter.types';
import { useToast } from '../../../../hooks/useToast';
import Modal from '../../../../components/shared/Modal/Modal';
import { Skeleton } from '../../../../components/shared/Skeleton/Skeleton';
import {
  listFixedAssets,
  listValuations,
  listDepreciationEntries,
  postDepreciationEntry
} from '../../../../services/FixedAssetsService';
import { ChartAccount, listChartOfAccounts } from '../../../../services/ChartOfAccountsService';
import { listCostCenters } from '../../../../services/CostCentersService';
import { formatDate, toIsoDate } from '../FinancesView/forms/FormParts';
import '../shared-modules.css';
import '../FinancesView/forms/FinanceForms.css';
import '../FinancesView/QuickActionsHub.css';
import './FixedAssetsView.css';

// Activo con las cuotas de su valoración contable
interface AssetSchedule {
  asset: FixedAsset;
  entries: DepreciationEntry[];
}

interface PostingRow {
  asset: FixedAsset;
  entry: DepreciationEntry;
  accountCode: string;
  costCenterCode: string;
  planned: number;
  posted: number;
}

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value || 0);

// En la tabla, como en SAP: el importe sin símbolo y la moneda en su columna
const CURRENCY = 'EUR';
const formatAmount = (value: number): string =>
  new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0);

// Último día del mes del periodo (YYYY-MM)
const postingDate = (period: string) => {
  const [y, m] = period.split('-').map(Number);
  return toIsoDate(new Date(y, m, 0));
};

const cuotas = (n: number) => `${n} ${n === 1 ? 'cuota' : 'cuotas'}`;

interface FixedAssetsMonthlyPostingProps {
  onClose: () => void;
}

export const FixedAssetsMonthlyPosting = ({ onClose }: FixedAssetsMonthlyPostingProps) => {
  const { notify } = useToast();

  const [period, setPeriod] = useState(toIsoDate(new Date()).slice(0, 7));
  const [schedules, setSchedules] = useState<AssetSchedule[]>([]);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [tested, setTested] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);

  // Se cargan las cuotas de todos los ejercicios: al cambiar de mes solo se
  // recalcula la tabla, sin volver al servidor.
  const fetchSchedules = async () => {
    setLoading(true);
    try {
      const assets = (await listFixedAssets()).filter(a => a.status === 'active');
      // Valoraciones y calendario solo admiten un activo por llamada: van en paralelo.
      const list = await Promise.all(assets.map(async (asset): Promise<AssetSchedule> => {
        const valuation = (await listValuations(asset.id)).filter(v => v.valuationArea === 'accounting')[0];
        return { asset, entries: valuation ? await listDepreciationEntries(valuation.id) : [] };
      }));
      setSchedules(list);
      setLoadError('');
    } catch (err) {
      console.error('Error fetching depreciation schedules:', err);
      setSchedules([]);
      setLoadError(err instanceof Error ? err.message : 'No se pudieron cargar las cuotas de amortización.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
    // Solo dan los códigos de cuenta y centro: si fallan, se muestra el nombre.
    listChartOfAccounts().then(setAccounts).catch(e => console.error('Error fetching chart of accounts:', e));
    listCostCenters().then(setCostCenters).catch(e => console.error('Error fetching cost centers:', e));
  }, []);

  const [year, month] = period ? period.split('-').map(Number) : [0, 0];
  const periodLabel = period ? `${period.slice(5, 7)}/${period.slice(0, 4)}` : '—';

  // Activos con la cuota del periodo todavía prevista
  const rows: PostingRow[] = [];
  schedules.forEach(({ asset, entries }) => {
    const entry = entries.filter(e => e.status === 'planned' && e.fiscalYear === year && e.period === month)[0];
    if (!entry) return;
    const account = asset.assetAccountId ? accounts.filter(a => a.id === asset.assetAccountId)[0] : undefined;
    const costCenter = asset.costCenterId ? costCenters.filter(c => c.id === asset.costCenterId)[0] : undefined;
    rows.push({
      asset,
      entry,
      accountCode: account ? account.code : asset.assetAccountName,
      costCenterCode: costCenter ? costCenter.code : asset.costCenterName,
      planned: entries.reduce((sum, e) => sum + e.plannedAmount, 0),
      posted: entries.filter(e => e.status === 'posted').reduce((sum, e) => sum + e.plannedAmount, 0)
    });
  });

  const total = (pick: (row: PostingRow) => number) => rows.reduce((sum, row) => sum + pick(row), 0);
  const totalToPost = total(row => row.entry.plannedAmount);

  // Solo cuentan año y mes: el día vuelve siempre a 01. Se ignora lo que no
  // sea una fecha entera con año de 4 cifras (llega vacía al borrar un trozo):
  // así no se pierde lo que ya había.
  const handlePeriodChange = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    setPeriod(value.slice(0, 7));
    setTested(false);
  };

  // El backend no comprueba si la cuota ya estaba contabilizada (sumaría dos
  // veces a la amortización acumulada): cada una se manda una sola vez y la
  // pantalla queda bloqueada hasta terminar.
  const confirmPost = async () => {
    setConfirmOpen(false);
    const pending = rows;
    setPosting(true);
    const failed: string[] = [];
    await Promise.all(pending.map(row =>
      postDepreciationEntry(row.entry.id).catch(err => {
        console.error('Error posting depreciation entry:', err);
        failed.push(row.asset.code);
      })
    ));
    const done = pending.length - failed.length;
    if (done > 0) notify('Éxito', `Cuotas de ${periodLabel} contabilizadas: ${done}`, 'info');
    if (failed.length > 0) notify('Error', `No se pudo contabilizar la cuota de: ${failed.join(', ')}`, 'urgent');
    setPosting(false);
    setTested(false);
    fetchSchedules();
  };

  return (
    <>
      {/* Volver arriba a la izquierda, igual que en las pantallas de creación de Finanzas */}
      <div className="qa-back-bar">
        <div className="qa-back-bar-left">
          <button type="button" onClick={onClose} disabled={posting} className="qa-back-btn">
            <ArrowLeft size={14} /> Volver
          </button>
          <div>
            <span className="qa-back-bar-category">CONTABILIDAD</span>
            <h2 className="qa-back-bar-title">Contabilizar Activos</h2>
          </div>
        </div>
        <span className="qa-back-bar-hint">Contabilización mensual de amortizaciones</span>
      </div>

      <div className="fin-doc">
        <div className="fin-doc__header">
          <div>
            <span className="fin-doc__tag">ACTIVOS FIJOS • AMORTIZACIONES</span>
            <h3 className="fin-doc__title"><Calculator size={14} /> Contabilización de amortizaciones</h3>
          </div>
          {tested && <span className="fin-doc__badge">EJECUCIÓN TEST</span>}
        </div>

        <div className="fin-doc__grid">
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Período *</label>
            <input
              type="date"
              required
              max="9999-12-31"
              className="sidebar-form-input"
              value={`${period}-01`}
              disabled={posting}
              onChange={(e) => handlePeriodChange(e.target.value)}
            />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Fecha de contabilización</label>
            <input
              type="text"
              readOnly
              className="sidebar-form-input fin-doc__mono fin-doc__readonly"
              value={period ? `${formatDate(postingDate(period))} (Automática)` : '—'}
            />
          </div>
        </div>

        <div className="fixed-assets-view__table-wrapper">
          <table className="fixed-assets-view__table">
            <thead>
              <tr>
                <th>CÓDIGO</th>
                <th>CTA. MAYOR</th>
                <th>CE. COSTE</th>
                <th>DENOMINACIÓN</th>
                <th className="right">IMPTE. PREVISTO</th>
                <th className="right">IMPTE. CONTABIL.</th>
                <th className="right">A CONTAB.</th>
                <th className="right">IMPTE. CONT. AC.</th>
                <th className="center">MON.</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="fixed-assets-view__table-row">
                    {Array.from({ length: 9 }).map((__, j) => (
                      <td key={j} className={j >= 4 ? 'right' : ''}><Skeleton /></td>
                    ))}
                  </tr>
                ))
              ) : loadError || rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="module-panel-empty">
                    {loadError || `No hay cuotas pendientes de contabilizar en ${periodLabel}.`}
                  </td>
                </tr>
              ) : (
                <>
                  {rows.map(row => (
                    <tr key={row.entry.id} className="fixed-assets-view__table-row">
                      <td><span className="fixed-assets-view__code">{row.asset.code}</span></td>
                      <td>{row.accountCode || '—'}</td>
                      <td>{row.costCenterCode || '—'}</td>
                      <td><span className="fixed-assets-view__name">{row.asset.name}</span></td>
                      <td className="right">{formatAmount(row.planned)}</td>
                      <td className="right">{formatAmount(row.posted)}</td>
                      <td className="right fixed-assets-view__to-post">{formatAmount(row.entry.plannedAmount)}</td>
                      <td className="right">{formatAmount(row.posted + row.entry.plannedAmount)}</td>
                      <td className="center">{CURRENCY}</td>
                    </tr>
                  ))}
                  <tr className="fixed-assets-view__table-total">
                    <td colSpan={4}>TOTAL ({rows.length})</td>
                    <td className="right">{formatAmount(total(row => row.planned))}</td>
                    <td className="right">{formatAmount(total(row => row.posted))}</td>
                    <td className="right fixed-assets-view__to-post">{formatAmount(totalToPost)}</td>
                    <td className="right">{formatAmount(total(row => row.posted + row.entry.plannedAmount))}</td>
                    <td className="center">{CURRENCY}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* De ejemplo hasta que el backend tenga la simulación: entonces saldrá de su respuesta */}
        {tested && (
          <div className="fin-doc__balance ok">
            <CheckCircle2 size={14} />
            {`Simulación correcta: ${cuotas(rows.length)} de ${periodLabel} por ${formatCurrency(totalToPost)}, con fecha de contabilización ${formatDate(postingDate(period))}.`}
          </div>
        )}

        <div className="fin-doc__actions">
          <button
            type="button"
            className="fin-doc__btn fin-doc__btn--outline"
            disabled={loading || posting || rows.length === 0}
            onClick={() => setTested(true)}
          >
            <PlayCircle size={12} /> Test
          </button>
          {tested && (
            <button
              type="button"
              className="fin-doc__btn fin-doc__btn--primary"
              disabled={posting}
              onClick={() => setConfirmOpen(true)}
            >
              <Check size={12} /> {posting ? 'Contabilizando...' : `Contabilizar (${rows.length})`}
            </button>
          )}
        </div>

        <Modal
          isOpen={confirmOpen}
          type="warning"
          title="Contabilizar amortizaciones"
          message={`Cuotas a contabilizar de ${periodLabel}: ${rows.length}, por ${formatCurrency(totalToPost)}. Esta acción no se puede deshacer.`}
          confirmText="Contabilizar"
          cancelText="Cancelar"
          onConfirm={confirmPost}
          onCancel={() => setConfirmOpen(false)}
        />
      </div>
    </>
  );
};

export default FixedAssetsMonthlyPosting;
